import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="font-display text-xl font-bold text-foreground">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-7 text-muted-foreground">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  useEffect(() => { document.title = "Privacy Policy | QR Code Creator Studio"; }, []);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <p className="text-sm font-semibold text-primary">LEGAL</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">Privacy Policy</h1>
        <p className="mt-3 text-sm text-muted-foreground">Last updated: 24 September 2026</p>

        <p className="mt-8 text-base leading-7 text-muted-foreground">
          QR Code Creator Studio respects your privacy. This policy explains what information we collect, why we collect it, how we use it, and the choices available to you when you use qrcodecreatorstudio.com and our QR code creation services.
        </p>

        <Section title="1. Information we collect">
          <p>We may collect account information such as your name, email address, company details, profile image and login provider information when you create an account or sign in with Google.</p>
          <p>We also collect the information you enter to create and manage QR codes, including destinations, QR code settings, team or employee contact details, uploaded files, and related account preferences.</p>
          <p>When a dynamic QR code is scanned, we may collect scan information such as the time of scan, device or browser information, IP address, approximate location derived from the IP address, and the QR code scanned. We use this information to provide scan analytics and keep the service secure.</p>
        </Section>

        <Section title="2. How we use information">
          <p>We use information to provide and improve the service, create and manage your account, generate QR codes, provide scan analytics, communicate with you, prevent abuse, troubleshoot technical issues, and comply with legal obligations.</p>
          <p>We do not sell your personal information.</p>
        </Section>

        <Section title="3. Service providers">
          <p>We use trusted service providers to operate the platform. These may include Google for sign-in, MongoDB Atlas for application data, Cloudflare R2 for file storage, our hosting provider, and payment providers when payments are enabled.</p>
          <p>Payment card details are processed by the relevant payment provider. QR Code Creator Studio does not store full payment card details on its own servers.</p>
        </Section>

        <Section title="4. Cookies and similar technologies">
          <p>We use essential cookies to keep you signed in and protect your account. We may also use analytics or performance technologies to understand how the service is used and improve it. You can manage cookies through your browser settings, although essential cookies are required for account features to work.</p>
        </Section>

        <Section title="5. Data retention and security">
          <p>We keep personal information only for as long as needed to provide the service, meet legal obligations, resolve disputes, and maintain security. We use reasonable administrative and technical safeguards to protect information, but no online system can be guaranteed completely secure.</p>
        </Section>

        <Section title="6. Your choices and rights">
          <p>You can update certain account information from your account settings. You may request access, correction, deletion, or export of your personal information by contacting us. We may need to verify your identity before completing a request.</p>
        </Section>

        <Section title="7. Changes to this policy">
          <p>We may update this policy when our services or legal obligations change. The latest version will always be available on this page, with the updated date shown above.</p>
        </Section>

        <Section title="8. Contact">
          <p>For privacy questions or requests, contact us at <a className="text-primary font-semibold" href="mailto:qrcodecreatorstudio@gmail.com">qrcodecreatorstudio@gmail.com</a>.</p>
        </Section>
      </main>

      <PublicFooter />
    </div>
  );
}
